// ADR 30.9.26i §2.2 — the per-SDK lifecycle the consent panel drives.
//
// 6.9.26c constructed both SDKs once per page behind one `initialised` latch,
// which was enough while the only mid-session change was the account row
// *withdrawing* a consent. A learner who can grant and withdraw from
// `/account` needs each SDK to know where it is:
//
//   absent ──grant──▶ constructing ──import resolved, still constructing──▶ live
//                         │  └──no credential / import rejected──▶ absent
//                         └──withdrawal before the import resolves──▶ closed
//   live ──withdrawal──▶ closed          closed ──grant──▶ closed (next load)
//
// `constructing` is the window between the decision and the attach. Re-reading
// the state after the `import()` is what stops a withdrawal that lands
// mid-import leaving a live SDK behind (Context fact 3 — the start-up race
// `reconcileConsent`'s `GET` could already win).
//
// Both `index.ts` and `consentControl.ts` import this module and neither
// imports the other, so there is no cycle.

import { attachPostHog, attachSentry, detachAnalytics, detachCrashReports, reportNonFatal } from './capture'
import { redactPostHogEvent, redactSentryPayload } from './redact'
import { wasmFramesIntegration } from './wasmFrames'

/** The US ingest host both native clients already use, and 5.9.26k's CSP names. */
const POSTHOG_HOST = 'https://us.i.posthog.com'

/** The consent a lifecycle belongs to, spelt as the account row's fields are. */
export type ConsentSdk = 'analytics' | 'crash_reports'

/** Both, in the order `initTelemetry` constructs them. */
export const CONSENT_SDKS: readonly ConsentSdk[] = ['analytics', 'crash_reports']

/** The consent-record field each SDK is governed by. */
export const CONSENT_FIELD = {
  analytics: 'analytics_opt_out',
  crash_reports: 'crash_reports_opt_out',
} as const satisfies Record<ConsentSdk, string>

/** §2.2's four states. */
export type SdkState = 'absent' | 'constructing' | 'live' | 'closed'

/**
 * ADR 23.9.26u §12 — properties every event of this *window's* session carries
 * (today `display_mode`), registered from PostHog's own `loaded` callback: the
 * one point that precedes PostHog's initial `$pageview` as well as every
 * `capture()` of ours. `register_for_session`, never `register`: `register`
 * persists to storage the installed app shares with a browser tab of the same
 * origin, so the two windows would overwrite each other's `display_mode`.
 */
export type SessionProperties = Record<string, string>

const states: Record<ConsentSdk, SdkState> = { analytics: 'absent', crash_reports: 'absent' }

/**
 * §2.3 — bumped only by a successful learner grant for that SDK, so a
 * reconcile whose `GET` was in flight across the grant can tell the row it
 * received is older than the learner's choice.
 */
const epochs: Record<ConsentSdk, number> = { analytics: 0, crash_reports: 0 }

/** Kept from `initTelemetry`, so a mid-session PostHog carries `display_mode` too. */
let sessionProperties: SessionProperties = {}

/** `initTelemetry` runs once per page; a second call constructs nothing new. */
let booted = false

export function sdkState(sdk: ConsentSdk): SdkState {
  return states[sdk]
}

export function grantEpoch(sdk: ConsentSdk): number {
  return epochs[sdk]
}

export function bumpGrantEpoch(sdk: ConsentSdk): void {
  epochs[sdk] += 1
}

/** True exactly once per page: the first `initTelemetry` call. */
export function claimBoot(superProperties: SessionProperties): boolean {
  if (booted) return false
  booted = true
  sessionProperties = superProperties
  return true
}

/**
 * Read at call time rather than at module load. An absent credential means
 * "not configured" — a checkout of this repo, or a preview deploy without the
 * secrets — and the SDK is simply not constructed, which is also what lets a
 * test stub the pair with `vi.stubEnv` (a module-load read would be frozen by
 * the time any test ran).
 */
function credential(name: 'VITE_POSTHOG_KEY' | 'VITE_SENTRY_DSN'): string {
  return import.meta.env[name] ?? ''
}

/**
 * Which SDK an init outcome is about (ADR 8.9.26b §3).
 *
 * The two fail independently, so the tag is a discriminator rather than
 * decoration: a `sentry` tag *does* arrive whenever Sentry constructed and
 * PostHog did not.
 */
type TelemetrySdk = 'posthog' | 'sentry'

/**
 * What a construction attempt did *short of* rejecting.
 *
 * `no_credential` is the case ADR 8.9.26b G2 found in production: the deploy
 * workflow named neither `VITE_POSTHOG_KEY` nor `VITE_SENTRY_DSN`, so both
 * SDKs were absent from every bundle whatever the visitor consented to, and
 * the only trace was a silent early return. It is a distinct value from `ok`
 * because "not configured" and "constructed" are the two readings a flat
 * PostHog panel cannot otherwise be told apart under. `withdrawn` (30.9.26i
 * §2.2) is a construction a withdrawal overtook mid-import: nothing was
 * constructed, and nothing failed either.
 */
type InitOutcome = 'ok' | 'no_credential' | 'withdrawn'

async function initPostHog(): Promise<InitOutcome> {
  const key = credential('VITE_POSTHOG_KEY')
  if (key === '') return 'no_credential'
  const { default: posthog } = await import('posthog-js')
  // §2.2 — the gate read at `init` time is the one that decides.
  if (states.analytics !== 'constructing') return 'withdrawn'
  posthog.init(key, {
    api_host: POSTHOG_HOST,
    // Session replay is 30.7.26a's separate consent conversation and a much
    // larger payload; the web does not opt into it here.
    disable_session_recording: true,
    // ADR 1.10.26l — posthog-js loads remote config (config.js, then /config)
    // and lazy extensions from `us-assets.i.posthog.com`, which 6.9.26c §3a
    // deliberately does not name. The web reads no flag, survey or site app,
    // so the remote paths are switched off rather than the policy widened.
    advanced_disable_flags: true,
    disable_external_dependency_loading: true,
    disable_surveys: true,
    // advanced_disable_flags makes this client config final. autocapture
    // defaults to true, and only the blocked remote config kept it off, so
    // it is stated here rather than switched on as a side effect.
    autocapture: false,
    loaded: (instance) => {
      instance.register_for_session(sessionProperties)
      // §2.5 — constructed only under a granting gate, so PostHog's own
      // persisted opt-out (from a withdrawal on an earlier load) never
      // outvotes the web's consent record. Optional calls because the older
      // suites' `loaded` instances are two-method fakes.
      if (instance.has_opted_out_capturing?.()) instance.clear_opt_in_out_capturing?.()
    },
    // ADR 24.9.26h §6 — no `/s/<token>` leaves in any property.
    before_send: redactPostHogEvent,
  })
  attachPostHog(posthog)
  states.analytics = 'live'
  return 'ok'
}

/**
 * ADR 2.10.26q §5 — the deploy's commit and `production`, set by
 * `web-pages.yml`'s deploy job. Each is omitted when absent (local, preview),
 * which is the behaviour before this ADR, rather than sent as an empty string.
 */
function releaseIdentity(): { release?: string; environment?: string } {
  const release = import.meta.env.VITE_SENTRY_RELEASE ?? ''
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT ?? ''
  return {
    ...(release === '' ? {} : { release }),
    ...(environment === '' ? {} : { environment }),
  }
}

async function initSentry(): Promise<InitOutcome> {
  const dsn = credential('VITE_SENTRY_DSN')
  if (dsn === '') return 'no_credential'
  const sentry = await import('@sentry/react')
  if (states.crash_reports !== 'constructing') return 'withdrawn'
  sentry.init({
    dsn,
    ...releaseIdentity(),
    // ADR 2.10.26q §3 — `contexts.rust_panic` on a wasm trap. An integration
    // runs before `beforeSend`, so the panic string is redacted too.
    integrations: [wasmFramesIntegration()],
    // ADR 24.9.26h §6 — every string in an event or breadcrumb, not a field list.
    beforeSend: redactSentryPayload,
    beforeBreadcrumb: redactSentryPayload,
  })
  attachSentry(sentry)
  states.crash_reports = 'live'
  return 'ok'
}

/**
 * absent → constructing → live (or back to absent with nothing constructed).
 *
 * A rejected import or a missing credential returns the SDK to `absent`, so a
 * later grant tries again; a withdrawal that overtook the import has already
 * set `closed`, and that is left alone.
 */
export async function construct(sdk: ConsentSdk): Promise<InitOutcome> {
  states[sdk] = 'constructing'
  try {
    const outcome = await (sdk === 'analytics' ? initPostHog() : initSentry())
    if (outcome === 'no_credential') states[sdk] = 'absent'
    return outcome
  } catch (error) {
    if (states[sdk] === 'constructing') states[sdk] = 'absent'
    throw error
  }
}

/**
 * live/constructing → closed; absent stays absent. Synchronous, and only the
 * one SDK — the other's handle is not this consent's business.
 */
export function withdraw(sdk: ConsentSdk): void {
  if (states[sdk] === 'live' || states[sdk] === 'constructing') states[sdk] = 'closed'
  if (sdk === 'analytics') detachAnalytics()
  else detachCrashReports()
}

const REPORTED_AS: Record<ConsentSdk, TelemetrySdk> = { analytics: 'posthog', crash_reports: 'sentry' }

/**
 * ADR 8.9.26b §3 — the one failure trace a *consenting* session can leave.
 *
 * Sentry, never PostHog: reporting a telemetry failure through `capture()`
 * would put the diagnosis behind the same rail it is diagnosing, which is the
 * circularity that made G2 invisible for a month.
 *
 * Reported **after** `Promise.allSettled` rather than at the failure site, and
 * that ordering is load-bearing: `initPostHog`'s credential check returns
 * before `initSentry`'s dynamic import has resolved, so a report emitted there
 * would meet a `sentry` handle that is still null and vanish.
 *
 * The `sentry` half of this fingerprint is undeliverable by construction —
 * `reportNonFatal` is `sentry?.captureException`, so the SDK that failed is the
 * one that would have carried the news. §3 says so, `telemetryFailure.test.ts`
 * AC5 pins the silence, and the build-time assertion in `web-pages.yml` is what
 * actually catches that case.
 */
export function reportInitOutcomes(sdks: ConsentSdk[], settled: PromiseSettledResult<InitOutcome>[]): void {
  settled.forEach((outcome, index) => {
    const sdk = REPORTED_AS[sdks[index]]
    let reason: string | null = null
    if (outcome.status === 'rejected') {
      console.warn('telemetry init failed', outcome.reason)
      reason = 'rejected'
    } else if (outcome.value === 'no_credential') {
      reason = 'no_credential'
    }
    if (reason === null) return
    reportNonFatal(`telemetry init failed: ${sdk}`, ['web-telemetry-init-failed', sdk], {
      sdk,
      reason,
    })
  })
}

/** Test seam: back to a page that has constructed nothing. */
export function resetLifecycleForTests(): void {
  states.analytics = 'absent'
  states.crash_reports = 'absent'
  epochs.analytics = 0
  epochs.crash_reports = 0
  sessionProperties = {}
  booted = false
}
